import React, { useCallback } from 'react';
import Iconify from '@emfood/shared/src/components/iconify';

import {
  Box,
  Chip,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
} from '@mui/material';

import { SearchAndFilterData } from 'src/hooks/use-product-list';

import { useAttributes } from 'src/api/hooks/api-hooks';
import {
  AttributeType,
  getAttributesByEnum,
  getAttributeTypeLabel,
} from 'src/api/models/attributes';

const attributeTypes = [
  AttributeType.Allergen,
  AttributeType.Category,
  AttributeType.Certification,
  AttributeType.DietaryAndLifestyle,
  AttributeType.CountryOfOrigin,
];

interface MultiAttributeAutocompleteProps {
  filterData: SearchAndFilterData;
  onChange: (field: keyof SearchAndFilterData, value: any) => void;
}

export default function MultiAttributeAutocomplete({
  filterData,
  onChange,
}: MultiAttributeAutocompleteProps) {
  const { data: attributesData, isLoading, error } = useAttributes();

  const handleDelete = useCallback(
    (valueToDelete: string, type: AttributeType) => {
      const fieldName = getAttributesByEnum(type) as keyof SearchAndFilterData;
      const currentValues = (filterData[fieldName] || []) as string[];
      const updatedValues = currentValues.filter((value: string) => value !== valueToDelete);
      onChange(fieldName, updatedValues);
    },
    [filterData, onChange]
  );

  const handleChipKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, value: string, type: AttributeType) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDelete(value, type);
      }
    },
    [handleDelete]
  );

  if (isLoading) return <Box role="status">Loading attributes...</Box>;
  if (error) return <Box role="alert">Error loading attributes</Box>;

  const handleDeleteAll = () => {
    attributeTypes.forEach((type) => {
      const fieldName = getAttributesByEnum(type) as keyof SearchAndFilterData;
      onChange(fieldName, []);
    });
  };

  return (
    <Box width="100%">
      <Stack direction="column" spacing={2} justifyContent="center">
        {attributeTypes.map((type) => {
          const options =
            attributesData?.attributes.find((attr) => attr.attributeType === type)?.attributes ||
            [];
          const fieldName = getAttributesByEnum(type) as keyof SearchAndFilterData;
          const fieldValue = (filterData[fieldName] || []) as string[];

          return (
            <Box sx={{ flex: 1 }} key={type}>
              <Autocomplete
                multiple
                value={fieldValue}
                disableCloseOnSelect
                options={options.map((opt) => opt.name)}
                aria-label={`Select multiple ${fieldName} to filter`}
                id={`${fieldName}-autocomplete`}
                ListboxProps={{
                  id: `${fieldName}-listbox`,
                  role: 'listbox',
                  'aria-multiselectable': 'true',
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    const listbox = document.getElementById(`${fieldName}-listbox`);
                    if (listbox) {
                      const firstItem = listbox.querySelector('[role="option"]');
                      if (firstItem) {
                        (firstItem as HTMLElement)?.focus();
                      }
                    }
                  }
                }}
                onChange={(_event, value) => {
                  onChange(fieldName, value);
                  const input = document.getElementById(`${fieldName}-autocomplete`);
                  if (input) {
                    input.focus();
                  }
                }}
                renderOption={(props, option, { selected }) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
                  const { className, ...rest } = props;
                  return (
                    <Box
                      component="li"
                      {...rest}
                      key={option}
                      role="option"
                      aria-selected={selected}
                      aria-labelledby={`${fieldName}-${option}-text`}
                      sx={{
                        color: 'grey.700',
                        p: 2,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'action.focus',
                          // outline: '2px solid',
                          // outlineColor: 'primary.main',
                          // outlineOffset: '-2px',
                        },
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        sx={{
                          mr: 1,
                          color: 'grey.700',
                          '&.Mui-checked': {
                            color: 'grey.800',
                          },
                        }}
                        inputProps={{
                          'aria-label': selected ? `Remove ${option}` : `Select ${option}`,
                          role: 'checkbox',
                        }}
                      />
                      <span id={`${fieldName}-${option}-text`}>{option}</span>
                    </Box>
                  );
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={getAttributeTypeLabel(type)}
                    placeholder={`Select ${getAttributeTypeLabel(type)}`}
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      'aria-controls': `${fieldName}-listbox`,
                      'aria-expanded': 'true',
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        color: 'grey.700',
                        '&:hover': {
                          color: 'grey.800',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'grey.700',
                        '&.Mui-focused': {
                          color: 'grey.800',
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.700',
                        '&:hover': {
                          borderColor: 'grey.800',
                        },
                        '&.Mui-focused': {
                          borderColor: 'grey.800',
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>
          );
        })}
      </Stack>
      <Box
        sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}
        aria-label="Active filters"
        role="region"
      >
        {attributeTypes.map((type) => {
          const fieldName = getAttributesByEnum(type) as keyof SearchAndFilterData;
          const values = (filterData[fieldName] || []) as string[];
          if (values.length === 0) return null;
          return (
            <Box
              key={type}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                borderStyle: 'dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
              }}
            >
              <Typography variant="h4" sx={{ mr: 1 }} component="h3">
                {getAttributeTypeLabel(type)}:
              </Typography>
              {values.map((value: string) => (
                <Chip
                  key={value}
                  label={value}
                  onDelete={() => handleDelete(value, type)}
                  onClick={() => handleDelete(value, type)}
                  onKeyDown={(e) => handleChipKeyDown(e, value, type)}
                  size="small"
                  tabIndex={0}
                  aria-label={`Remove ${value} from ${getAttributeTypeLabel(type)}`}
                  sx={{
                    '&:last-child': { mr: 1 },
                    '&:focus': {
                      backgroundColor: 'grey.300',
                      color: 'black',
                    },
                  }}
                />
              ))}
            </Box>
          );
        })}
        {attributeTypes.some(
          (type) =>
            ((filterData[getAttributesByEnum(type) as keyof SearchAndFilterData] || []) as string[])
              .length > 0
        ) && (
          <IconButton
            onClick={handleDeleteAll}
            size="small"
            aria-label="Clear all filters"
            sx={{
              '&:focus': {
                outline: '2px solid',
                outlineOffset: '2px',
              },
            }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            <Typography variant="subtitle2">Clear</Typography>
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
